/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import axios from 'axios';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import has from 'lodash/has';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { SecurityHelper as SecurityUtils } from 'sarsaparilla';
import { hasCartLimitExceededError } from 'ui-cart/src/utils/utils';
import * as types from '../../shared/constants';
import { authCampingAxios } from '../../shared/utils/campingAxios';
import { deltaParams } from '../../shared/utils/recaptcha';
import * as utils from '../../shared/utils/utils';
import { printPDF } from '../../shared/utils/utils';
import * as RecommendationActions from './recommendations/recommendationActions';

const GET_CART = 'GET_CART';
const CART_ERROR = 'CART_ERROR';

// This needs to be imported once cart team exported it to some common repo
export const getCart = (dispatch) =>
    axios
        .get(`${process.env.API}/cart/shoppingcart`, {
            headers: { Authorization: SecurityUtils.getAuthHeader() },
        })
        .then((response) => {
            dispatch({ type: GET_CART, payload: response.data });
        })
        .catch(() => {
            dispatch({
                type: CART_ERROR,
                payload: 'Error fetching cart',
                error: true,
            });
        });

/**
 * Fetches a single reservation's details with provided reservationID
 * @param {string} reservationID - id of reservation to be retrieved
 */
export function fetchReservation(reservationID) {
    return (dispatch) => {
        return authCampingAxios
            .get(`/reservations/${reservationID}`)
            .then((response) => {
                dispatch({
                    type: types.GET_RESERVATION,
                    payload: response.data.reservation,
                });
                return Promise.resolve(response.data.reservation);
            })
            .catch((error) => {
                dispatch({
                    type: types.GET_RESERVATION_ERROR,
                    payload: get(
                        error,
                        'response.data.error',
                        'Error fetching reservation'
                    ),
                });
                return Promise.resolve(error.response);
            });
    };
}

export function fetchReservationRefund(reservationId, status, options) {
    return (dispatch) => {
        return authCampingAxios
            .post(`/reservations/${reservationId}/refund`, {
                status,
                options,
            })
            .then(utils.status)
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch(
                        utils.requestFailure(
                            types.GET_RESERVATION_REFUND_ERROR,
                            error.response.data.error
                        )
                    );
                    return Promise.reject(error.response.data.error);
                }
                return Promise.resolve(error);
            })
            .then((json) => json.data)
            .then((response) => {
                const payload = { ...response };
                dispatch({ type: types.GET_RESERVATION_REFUND, payload });
                return Promise.resolve(payload);
            });
    };
}

export function fetchReservations(reservationIDs) {
    let idsStr = '';
    if (isArray(reservationIDs)) {
        forEach(reservationIDs, (id) => {
            idsStr += idsStr === '' ? '?' : '&';
            idsStr += `id=${id}`;
        });
    } else {
        idsStr = `?id=${reservationIDs}`;
    }

    return (dispatch) => {
        return authCampingAxios
            .get(`/reservations${idsStr}`)
            .then((response) => {
                dispatch({ type: types.GET_RESERVATIONS, payload: response.data });
                return Promise.resolve(response);
            })
            .catch((error) => {
                dispatch({
                    type: types.GET_RESERVATIONS_ERROR,
                    payload: get(
                        error,
                        'response.data.error',
                        'Error fetching reservations'
                    ),
                });
                return Promise.resolve(error);
            });
    };
}

export function createReservations(reservations, facilityId, recaptcha) {
    const alphaRecaptcha = deltaParams(recaptcha);

    const requestBody = {
        reservations,
    };

    reservations.forEach((res) => {
        // check and set recommendation referrer on the order, if reservation is linked to click on the recommendations
        if (isEmpty(res.reservation_options)) {
            res.reservation_options = {};
        }
        res.reservation_options.recommendation_referrer =
            RecommendationActions.getRecommendationReferrerForLocation(facilityId);
    });

    if (!isEmpty(alphaRecaptcha)) {
        requestBody.gate_a = alphaRecaptcha;
    }

    return (dispatch) =>
        authCampingAxios
            .post(`/reservations/campgrounds/${facilityId}/multi`, requestBody)
            .then(utils.status)
            .catch((error) => {
                if (error.response && error.response.data) {
                    // TODO: figure out a better way to send the error down...
                    // doing this for now since there's a lot of error logic that
                    // needs to be figured out by components using this...
                    if (hasCartLimitExceededError(error.response.data.message)) {
                        return Promise.reject(error.response.data.message);
                    }

                    dispatch(
                        utils.requestFailure(
                            types.CREATE_RESERVATIONS_ERROR,
                            error.response.data.error
                        )
                    );
                    return Promise.reject(error.response.data.error);
                }
                return Promise.resolve(error);
            })
            .then((json) => json.data)
            .then((response) => {
                getCart(dispatch);
                // record convergence event into the AB tracking tools for recommendations
                dispatch(RecommendationActions.checkLogAddToCartConversion(facilityId));
                return Promise.resolve(response);
            });
}

export function updateReservation(reservationID, occupantData, checkin, checkout) {
    return (dispatch) =>
        authCampingAxios
            .put(`/reservations/${reservationID}`, {
                check_in: checkin,
                check_out: checkout,
                site_occupant: occupantData,
            })
            .then(utils.status)
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch(
                        utils.requestFailure(
                            types.UPDATE_RESERVATION_ERROR,
                            error.response.data.error
                        )
                    );
                    return Promise.reject(error.response.data.error);
                }
                return Promise.resolve(error);
            })
            .then((json) => json.data)
            .then((response) => {
                dispatch({
                    type: types.UPDATE_RESERVATION,
                    payload: response.reservation,
                });
                return Promise.resolve(response.reservation);
            });
}

export function modifyReservation(
    reservationID,
    campsiteID,
    occupantData,
    checkin,
    checkout,
    reservationOptions = {}
) {
    return (dispatch) =>
        authCampingAxios
            .put(`/reservations/${reservationID}/modify`, {
                reservation_id: reservationID,
                campsite_id: campsiteID,
                check_in: checkin,
                check_out: checkout,
                site_occupant: occupantData,
                reservation_options: reservationOptions,
            })
            .then(utils.status)
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch(
                        utils.requestFailure(
                            types.UPDATE_RESERVATION_ERROR,
                            error.response.data.error
                        )
                    );
                    return Promise.reject(error.response.data.error);
                }
                return Promise.resolve(error);
            })
            .then((json) => json.data)
            .then((response) => {
                dispatch({
                    type: types.UPDATE_RESERVATION,
                    payload: response.reservation,
                });
                return Promise.resolve(response.reservation);
            });
}

export function updateReservationStatus(reservationId, status, options) {
    return (dispatch) => {
        return authCampingAxios
            .put(`/reservations/${reservationId}/status`, {
                reservation_id: reservationId,
                status,
                options,
            })
            .then(utils.status)
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch(
                        utils.requestFailure(
                            types.RESERVATION_UPDATE_STATUS_ERROR,
                            error.response.data.error
                        )
                    );
                    return Promise.reject(error.response.data.error);
                }
                return Promise.resolve(error);
            })
            .then((json) => json.data)
            .then((response) => {
                const payload = response;
                dispatch({
                    type: types.RESERVATION_UPDATE_STATUS,
                    requestStatus: status,
                    balance: response,
                });
                return Promise.resolve(payload);
            });
    };
}

export function deleteReservation(reservationID) {
    return (dispatch) =>
        authCampingAxios
            .delete(`/reservations/${reservationID}`)
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.DELETE_RESERVATION_ERROR,
                        payload: error.response.data.error,
                    });
                }
                return Promise.resolve(error);
            })
            .then((response) => {
                dispatch({ type: types.DELETE_RESERVATION, payload: response.data.ok });
                return Promise.resolve(response);
            });
}

export function undoReservation(reservationID) {
    return (dispatch) =>
        axios
            .delete(`${process.env.API}/cart/shoppingcart/${reservationID}`, {
                headers: { Authorization: SecurityUtils.getAuthHeader() },
            })
            .catch((error) => {
                dispatch({
                    type: types.UNDO_RESERVATION_ERROR,
                    payload: error.response.data.error,
                });
                return Promise.resolve(error);
            })
            .then((response) => {
                dispatch({ type: types.GET_CART, payload: response.data });
                return Promise.resolve(response);
            });
}

export function clearReservationErrors() {
    return (dispatch) => dispatch({ type: types.CLEAR_RESERVATION_ERRORS });
}

export function saveReservations(reservations) {
    return (dispatch) => {
        dispatch({ type: types.SAVE_RESERVATIONS, payload: reservations });
    };
}

export function fetchReservationPriceDetails(
    reservationId,
    campsiteId,
    checkIn,
    checkOut,
    siteOccupant,
    nightMap = {}
) {
    return (dispatch) =>
        authCampingAxios
            .post(`/reservations/${reservationId}/price`, {
                reservation_id: reservationId,
                campsite_id: campsiteId,
                check_in: checkIn,
                check_out: checkOut,
                reservation_options: {
                    night_map: nightMap,
                },
                site_occupant: siteOccupant,
            })
            .then(utils.status)
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch(
                        utils.requestFailure(
                            types.GET_RESERVATION_PRICE_DETAILS_ERROR,
                            error.response.data.error
                        )
                    );
                    return Promise.reject(error.response.data.error);
                }
                return Promise.resolve(error);
            })
            .then((json) => json.data)
            .then((response) => {
                dispatch({
                    type: types.GET_RESERVATION_PRICE_DETAILS,
                    payload: response,
                });
                return Promise.resolve(response);
            });
}

export function reprocessReservation(reservationId) {
    return (dispatch) => {
        const urlWithParams = utils.urlWithParams(
            `/reservations/${reservationId}/reprocess`
        );
        return authCampingAxios
            .post(urlWithParams, {})
            .then(utils.status)
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({ type: types.GET_RESERVATION_ERROR, payload: error });
                    return Promise.reject(error.response.data.error);
                }
                return error;
            })
            .then((json) => json.data)
            .then((response) => {
                const payload = response;
                return Promise.resolve(payload);
            });
    };
}

export function printCampingPermitPDF(reservationId, reservationNumber) {
    const account = SecurityUtils.getAccount();
    const requestBody = {
        street1: get(account, 'home_address.address1', ''),
        street2: get(account, 'home_address.address2', ''),
        city: get(account, 'home_address.city', ''),
        state: get(account, 'home_address.state', ''),
        zip_code: get(account, 'home_address.zip_code', ''),
        country: get(account, 'home_address.country', ''),
        emergency_contact_name:
            get(account, 'emergency_contact.first_name', '') +
            get(account, 'emergency_contact.last_name', ''),
        emergency_contact_number: get(account, 'emergency_contact.phone', ''),
    };

    const errMessage =
        'Permit could not be printed, please contact the Help Center if this issue persists.';

    return (dispatch) =>
        authCampingAxios
            .post(`/reservations/${reservationId}/itinerary/pdf`, requestBody, {
                responseType: 'arraybuffer',
            })
            .catch((error) => {
                if (!isNil(error) && !isEmpty(error)) {
                    if (has(error.response) && has(error.response.data)) {
                        dispatch({
                            type: types.GET_CAMPING_PERMIT_PDF_ERROR,
                            payload: errMessage,
                            error: error.response.data,
                        });
                        return Promise.reject(error.response.data.error);
                    }

                    dispatch({
                        type: types.GET_CAMPING_PERMIT_PDF_ERROR,
                        payload: errMessage,
                        error,
                    });
                    return Promise.reject(error);
                }

                return Promise.resolve(error);
            })
            .then((response) => {
                const blob = new Blob([response.data], {
                    type: 'application/pdf; charset=utf-8',
                });
                printPDF(blob, `${reservationNumber}_permit.pdf`);
            });
}
